import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Info } from 'lucide-react';
import { getFunnelGeoRevenue, GeoRevenueCell } from '@/services/api/meta-analytics';
import { fmtMoney, fmtN } from './utils';

const COUNTRY_LABEL: Record<string, string> = {
    Netherlands: 'Holanda', Germany: 'Alemanha', Italy: 'Itália',
    France: 'França', Spain: 'Espanha', Canada: 'Canadá',
    Switzerland: 'Suíça', Belgium: 'Bélgica', Austria: 'Áustria',
    'United Kingdom': 'Reino Unido', 'United States': 'EUA',
    Australia: 'Austrália', Portugal: 'Portugal', Brazil: 'Brasil',
    Mexico: 'México', Argentina: 'Argentina', Colombia: 'Colômbia',
    Chile: 'Chile', Peru: 'Peru', Croatia: 'Croácia', Romania: 'Romênia',
};

function cellBg(rev: number, maxRev: number): string {
    if (rev <= 0) return 'bg-gray-50';
    const i = rev / maxRev;
    if (i > 0.6) return 'bg-indigo-600 text-white';
    if (i > 0.3) return 'bg-indigo-400 text-white';
    if (i > 0.1) return 'bg-indigo-200 text-indigo-900';
    return 'bg-indigo-50 text-indigo-800';
}

function Tooltip({ children, tip }: { children: React.ReactNode; tip: React.ReactNode }) {
    return (
        <div className="relative group inline-block">
            {children}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50
                            bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-xl pointer-events-none min-w-max">
                {tip}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
            </div>
        </div>
    );
}

export function FunnelGeoRevenueMatrix({ start, end }: { start: string; end: string }) {
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['funnel-geo-revenue', start, end],
        queryFn: () => getFunnelGeoRevenue(start, end),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        retry: 1,
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Carregando revenue por país × domain (funnels)...</span>
            </div>
        );
    }

    if (isError || data?.success === false) {
        return (
            <div className="flex items-center gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800 flex-1">Erro ao carregar geo-revenue de funnels.</p>
                <button onClick={() => refetch()} className="text-sm text-amber-700 font-medium underline">Tentar novamente</button>
            </div>
        );
    }

    const geoData = data?.data;
    if (!geoData || geoData.cells.length === 0) {
        return (
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-10 text-center">
                <p className="text-gray-400 text-sm">Sem dados de revenue por país para funnels no período.</p>
                <p className="text-gray-300 text-xs mt-1">Verifique se os funnels estão com utm_content=flow_&#123;id&#125; configurado.</p>
            </div>
        );
    }

    const { cells, domains, countries, country_totals, domain_totals, no_revenue_domains } = geoData;

    const cellMap: Record<string, GeoRevenueCell> = {};
    for (const c of cells) cellMap[`${c.domain}|${c.country}`] = c;

    const maxRev = Math.max(...cells.map(c => c.revenue), 0.01);
    const domainsNoRevenue = no_revenue_domains ?? domains.filter(d => !domain_totals[d]?.has_revenue);
    const domainsWithRevenue = domains.filter(d => !domainsNoRevenue.includes(d));
    const domainsSorted = [...domainsWithRevenue, ...domainsNoRevenue];

    return (
        <div className="space-y-4">
            {domainsNoRevenue.length > 0 && (
                <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                    <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-red-700">
                            {domainsNoRevenue.length} domain{domainsNoRevenue.length > 1 ? 's' : ''} sem revenue
                        </p>
                        <p className="text-xs text-red-600 mt-0.5">{domainsNoRevenue.join(', ')}</p>
                    </div>
                </div>
            )}

            <div className="flex items-start gap-2 text-xs text-gray-400">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Revenue real do key-values filtrado por utm_content=flow_&#123;id&#125;</span>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-40 sticky left-0 bg-gray-50 z-10">
                                País / Domain
                            </th>
                            {domainsSorted.map(domain => {
                                const dt = domain_totals[domain];
                                return (
                                    <th key={domain} className="px-3 py-2 text-center min-w-[130px]">
                                        <div className="flex flex-col items-center gap-1">
                                            <div className="flex items-center gap-1">
                                                {dt?.has_revenue
                                                    ? <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                                                    : <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                                                }
                                                <span className="text-xs font-semibold text-gray-700 truncate max-w-[110px]">{domain}</span>
                                            </div>
                                            {dt?.has_revenue ? (
                                                <span className="text-[10px] text-indigo-600 font-medium">
                                                    {fmtMoney(dt.revenue)} · eCPM {fmtMoney(dt.ecpm)}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] text-red-400 font-medium">Sem revenue</span>
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase bg-gray-100">Total</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {countries.map(country => {
                            const ct = country_totals[country];
                            return (
                                <tr key={country} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-4 py-2.5 sticky left-0 bg-white z-10 border-r border-gray-100">
                                        <div>
                                            <p className="font-medium text-gray-800 text-xs">{COUNTRY_LABEL[country] ?? country}</p>
                                            <p className="text-[10px] text-gray-400">{fmtN(ct.impressions)} imp · {fmtN(ct.clicks)} cliques</p>
                                        </div>
                                    </td>
                                    {domainsSorted.map(domain => {
                                        const cell = cellMap[`${domain}|${country}`];
                                        if (!cell) {
                                            return <td key={domain} className="px-3 py-2.5 text-center bg-white"><span className="text-gray-200 text-xs">–</span></td>;
                                        }
                                        const bg = cellBg(cell.revenue, maxRev);
                                        return (
                                            <td key={domain} className={`px-2 py-2 text-center ${bg}`}>
                                                <Tooltip tip={
                                                    <div className="space-y-1">
                                                        <p className="font-semibold">{domain} × {COUNTRY_LABEL[country] ?? country}</p>
                                                        <p>Revenue: {fmtMoney(cell.revenue)}</p>
                                                        <p>Impressões: {fmtN(cell.impressions)}</p>
                                                        <p>Cliques ad: {fmtN(cell.clicks)}</p>
                                                        <p>eCPM: {fmtMoney(cell.ecpm)}</p>
                                                        <p>CTR ad: {cell.ctr.toFixed(1)}%</p>
                                                        <p>Rev/clique: {fmtMoney(cell.rpc)}</p>
                                                    </div>
                                                }>
                                                    <div className="cursor-default">
                                                        <p className="text-xs font-bold leading-tight">{fmtMoney(cell.revenue)}</p>
                                                        <p className="text-[10px] opacity-80 leading-tight">{fmtN(cell.impressions)}imp</p>
                                                    </div>
                                                </Tooltip>
                                            </td>
                                        );
                                    })}
                                    <td className="px-3 py-2.5 text-center bg-gray-50 border-l border-gray-100">
                                        <p className="text-xs font-bold text-gray-800">{fmtMoney(ct.revenue)}</p>
                                        <p className="text-[10px] text-gray-400">{fmtN(ct.impressions)} imp</p>
                                    </td>
                                </tr>
                            );
                        })}
                        <tr className="bg-gray-100 border-t-2 border-gray-200">
                            <td className="px-4 py-2.5 sticky left-0 bg-gray-100 z-10 border-r border-gray-200">
                                <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Total</p>
                            </td>
                            {domainsSorted.map(domain => {
                                const dt = domain_totals[domain];
                                return (
                                    <td key={domain} className="px-3 py-2.5 text-center">
                                        <p className="text-xs font-bold text-gray-800">{fmtMoney(dt?.revenue ?? 0)}</p>
                                        <p className="text-[10px] text-gray-500">{fmtN(dt?.impressions ?? 0)} imp</p>
                                    </td>
                                );
                            })}
                            <td className="px-3 py-2.5 text-center bg-gray-200 border-l border-gray-300">
                                <p className="text-xs font-bold text-gray-900">
                                    {fmtMoney(Object.values(domain_totals).reduce((s, d) => s + d.revenue, 0))}
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-indigo-600" /><span>Revenue alto</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-indigo-200" /><span>Revenue baixo</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-gray-50 border border-gray-200" /><span>Sem dados</span></div>
                <span className="ml-auto text-gray-400">Fonte: key-values real · imp = impressões de ad</span>
            </div>
        </div>
    );
}
